// Text search + filter predicates shared by the header search, listing pages and menus.
const STOP = new Set(["and", "of", "the", "all", "tour", "tours", "package", "packages", "holiday", "holidays", "trip", "trips", "in", "to", "a", "for", "with", "only", "best"]);

const ALIASES = {
  usa: ["usa", "united states", "america"],
  "united": ["united", "usa"],
  states: ["states", "usa"],
  uae: ["uae", "dubai", "abu dhabi", "emirates"],
  emirates: ["emirates", "dubai", "abu dhabi"],
  middleeast: ["middle east", "dubai"],
  orissa: ["orissa", "odisha", "puri", "konark", "bhubaneswar", "bhubaneshwar"],
  odisha: ["odisha", "orissa", "puri", "konark"],
  bhubaneshwar: ["bhubaneshwar", "bhubaneswar"],
  bangalore: ["bangalore", "bengaluru"],
  bengaluru: ["bengaluru", "bangalore"],
  cochin: ["cochin", "kochi"],
  kochi: ["kochi", "cochin"],
  janerio: ["janeiro", "janerio"],
  vizag: ["vizag", "visakhapatnam"],
  himachal: ["himachal", "shimla", "manali"],
  kashmir: ["kashmir", "srinagar", "gulmarg"],
  andaman: ["andaman", "port blair", "havelock"],
  england: ["england", "london", "united kingdom"],
  korea: ["korea", "seoul"],
  thiruvananthapuram: ["thiruvananthapuram", "trivandrum"],
  pondicherry: ["pondicherry", "puducherry"],
  scandinavia: ["scandinavia", "norway", "sweden", "denmark", "finland"],
  women: ["women", "woman", "ladies"],
  senior: ["senior", "seniors"],
  seniors: ["seniors", "senior"],
  cherry: ["cherry", "japan"],
  blossom: ["blossom", "japan"],
};

const REGION_SYNONYMS = {
  "South East Asia": ["south east asia", "thailand", "singapore", "malaysia", "bali", "indonesia", "vietnam", "cambodia", "laos", "philippines"],
  "North East": ["north east", "assam", "meghalaya", "sikkim", "arunachal", "nagaland", "tripura", "manipur", "mizoram", "darjeeling"],
  "Nainital Mussoorie Corbett": ["nainital", "mussoorie", "corbett"],
};

export const norm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const tokenize = (q) =>
  norm(q).split(" ").filter((w) => w.length > 1 && !STOP.has(w));

const cache = new WeakMap();
const hay = (t) => {
  if (cache.has(t)) return cache.get(t);
  const states = (t.route || []).map((r) => r.state).join(" ");
  const stops = (t.route || []).flatMap((r) => r.stops || []).join(" ");
  const h = {
    title: ` ${norm(t.title)} `,
    place: ` ${norm([t.region, t.zone, states, stops, t.scope === "india" ? "india" : ""].join(" "))} `,
    rest: ` ${norm([
      t.speciality, t.packageType, t.interest, t.code, t.tourType, t.departureCity,
      (t.highlights || []).join(" "), (t.itinerary || []).map((d) => d.title).join(" "),
    ].join(" "))} `,
  };
  cache.set(t, h);
  return h;
};

const hasWord = (text, alt, prefix) =>
  prefix ? text.includes(` ${alt}`) : text.includes(` ${alt} `) || text.includes(` ${alt}s `);

const tokenScore = (h, token, prefix = false) => {
  const alts = ALIASES[token] || [token];
  let best = 0;
  alts.forEach((alt) => {
    if (hasWord(h.title, alt, prefix)) best = Math.max(best, 5);
    else if (hasWord(h.place, alt, prefix)) best = Math.max(best, 3);
    else if (hasWord(h.rest, alt, prefix)) best = Math.max(best, 1);
  });
  return best;
};

const phraseOf = (tokens, prefix) => (tokens.length > 1 ? ` ${tokens.join(" ")}${prefix ? "" : " "}` : "");
const hasPhrase = (h, phrase) => phrase && (h.title.includes(phrase) || h.place.includes(phrase) || h.rest.includes(phrase));

// Returns tours ranked by relevance. All words must match; if nothing does, falls back to any word.
// ── Budget phrases: "under 40000", "below 40k", "above 1 lakh", "between 30000 and 50000", "30k-50k"
const AMOUNT = String.raw`(?:rs\.?|inr|₹)?\s*(\d+(?:[.,]\d+)*)\s*(k|thousand|l|lac|lakh|lakhs|lacs)?\b`;
const toAmount = (num, unit) => {
  const n = parseFloat(String(num).replace(/,/g, ""));
  const u = (unit || "").toLowerCase();
  if (u === "k" || u === "thousand") return Math.round(n * 1000);
  if (u.startsWith("l")) return Math.round(n * 100000);
  return Math.round(n);
};
const BUDGET_RULES = [
  { re: new RegExp(String.raw`\bbetween\s+${AMOUNT}\s*(?:and|to|-)\s*${AMOUNT}`, "i"), get: (m) => [toAmount(m[1], m[2]), toAmount(m[3], m[4])] },
  { re: new RegExp(String.raw`${AMOUNT}\s*(?:-|to)\s*${AMOUNT}`, "i"), get: (m) => [toAmount(m[1], m[2]), toAmount(m[3], m[4])] },
  { re: new RegExp(String.raw`(?:\bunder|\bbelow|\bless than|\bup ?to|\bwithin|\bmax(?:imum)?|\bbudget(?: of)?|<=?)\s*${AMOUNT}`, "i"), get: (m) => [0, toAmount(m[1], m[2])] },
  { re: new RegExp(String.raw`(?:\babove|\bover|\bmore than|\bmin(?:imum)?|\bstarting|\bfrom|>=?)\s*${AMOUNT}`, "i"), get: (m) => [toAmount(m[1], m[2]), Infinity] },
];

export const parseBudget = (q) => {
  const raw = String(q ?? "");
  for (const rule of BUDGET_RULES) {
    const m = raw.match(rule.re);
    if (!m) continue;
    if (/^\s*(days?|nights?|d|n)\b/i.test(raw.slice(m.index + m[0].length))) continue;
    const [a, b] = rule.get(m);
    const [min, max] = a <= b ? [a, b] : [b, a];
    if (max < 500) continue;
    const text = raw.replace(m[0], " ").replace(/\b(price|priced|cost|costing|budget|rs|inr)\b/gi, " ").replace(/\s+/g, " ").trim();
    return { text, min, max, label: m[0].trim() };
  }
  return { text: raw.trim(), min: null, max: null, label: "" };
};

const inBudget = (b) => (t) => b.min == null || (t.startingPrice != null && t.startingPrice >= b.min && t.startingPrice <= b.max);

const formatAmount = (n) => "₹" + Number(n).toLocaleString("en-IN");
export const describeBudget = (b) => {
  if (b.min == null) return "";
  if (b.max === Infinity) return `Above ${formatAmount(b.min)}`;
  if (b.min === 0) return `Under ${formatAmount(b.max)}`;
  return `${formatAmount(b.min)} – ${formatAmount(b.max)}`;
};

// `prefix` lets the last word match word-starts (used while typing). Budget phrases filter by starting price.
export const searchTours = (tours, q, opts = {}) => {
  const b = parseBudget(q);
  return rankTours(tours, b.text, opts).filter(inBudget(b));
};

const rankTours = (tours, q, { prefix = false } = {}) => {
  const tokens = tokenize(q);
  if (tokens.length === 0) return tours;
  const phrase = phraseOf(tokens, prefix);
  const scored = tours.map((t) => {
    const h = hay(t);
    const scores = tokens.map((tk, i) => tokenScore(h, tk, prefix && i === tokens.length - 1));
    return { t, phrase: hasPhrase(h, phrase), hits: scores.filter(Boolean).length, score: scores.reduce((a, b) => a + b, 0) };
  });
  const phraseHits = scored.filter((s) => s.phrase);
  if (phraseHits.length) return phraseHits.sort((a, b) => b.score - a.score).map((s) => s.t);
  let matched = scored.filter((s) => s.hits === tokens.length);
  if (matched.length === 0) matched = scored.filter((s) => s.hits > 0);
  return matched.sort((a, b) => b.hits - a.hits || b.score - a.score).map((s) => s.t);
};

export const isExactMatch = (allTours, q, { prefix = false } = {}) => {
  const b = parseBudget(q);
  const tours = allTours.filter(inBudget(b));
  const tokens = tokenize(b.text);
  if (tokens.length === 0) return tours.length > 0;
  return tours.some((t) => tokens.every((tk, i) => tokenScore(hay(t), tk, prefix && i === tokens.length - 1) > 0));
};

const placeMatch = (t, label) => {
  const tokens = tokenize(label);
  const h = hay(t);
  return tokens.length > 0 && tokens.every((tk) => tokenScore({ title: h.title, place: h.place, rest: "" }, tk) > 0);
};

export const matchRegion = (t, label, allLabel) =>
  label === allLabel || t.region === label || t.zone === label ||
  (REGION_SYNONYMS[label] || label.split("&")).some((part) => placeMatch(t, part));

const stripParen = (s) => norm(String(s).replace(/\(.*\)/, ""));

export const matchDeparture = (t, label) => {
  const cities = [t.departureCity, ...(t.departureCities || [])].filter(Boolean);
  if (/joining/i.test(label)) return cities.some((c) => /joining/i.test(c));
  const want = (ALIASES[norm(label)] || [norm(label)]).concat(norm(label) === "new delhi" ? ["delhi"] : []);
  return cities.some((c) => want.includes(stripParen(c)) || want.includes(stripParen(c).replace(/^new /, "")));
};

export const matchPackageType = (t, label) =>
  label === "All" || norm(t.packageType).startsWith(norm(label).replace(/s$/, ""));

export const matchSpeciality = (t, label) => norm(t.speciality).startsWith(norm(label));

export const matchPlace = placeMatch;

export const matchCity = (t, label) => {
  const tokens = tokenize(label);
  const h = hay(t);
  return tokens.length > 0 && tokens.every((tk) => tokenScore(h, tk) > 0);
};

// "3 - 15 Days" -> [3, 15], "10+ Days" / "More than 16 days" -> [n, Infinity], "Less than 6 days" -> [0, n]
export const parseDayRange = (label) => {
  const nums = String(label).match(/\d+/g)?.map(Number) || [];
  if (/more than|above/i.test(label)) return [(nums[0] ?? 0) + 1, Infinity];
  if (/\+/.test(label)) return [nums[0] ?? 0, Infinity];
  if (/less than|under|below/i.test(label)) return [0, nums[0] != null ? nums[0] - 1 : Infinity];
  return [nums[0] ?? 0, nums[1] ?? nums[0] ?? Infinity];
};

export const departsBetween = (t, from, to) => {
  const start = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
  const end = to ? new Date(`${to}T00:00:00`).getTime() + 86400000 : Infinity;
  return (t.dates || []).some((d) => {
    const ts = new Date(d.date).getTime();
    return !Number.isNaN(ts) && ts >= start && ts < end;
  });
};

const priceOf = (t, fallback) => (t.startingPrice == null ? fallback : t.startingPrice);
export const SORTERS = {
  "Price: High to Low": (a, b) => priceOf(b, -1) - priceOf(a, -1),
  "Price: Low to High": (a, b) => priceOf(a, Infinity) - priceOf(b, Infinity),
  "Duration: Long to Short": (a, b) => b.days - a.days,
  "Duration: Short to Long": (a, b) => a.days - b.days,
};

export const sortTours = (list, sort) => (SORTERS[sort] ? [...list].sort(SORTERS[sort]) : list);

export const matchJoining = (t, label) =>
  (t.departureCities || []).some((c) => /joining/i.test(c) && stripParen(c) === norm(label));

export const matchText = matchCity;

const recount = (list, tours, fn) =>
  (list || []).map((x) => ({ ...x, count: tours.filter((t) => fn(t, x.label)).length })).filter((x) => x.count > 0);

// Facet options with counts computed from the actual tours (zero-count options hidden).
export const dedupeAliases = (list, key = "label") =>
  list.filter((x) => !(norm(x[key]) === "bangalore" && list.some((y) => norm(y[key]) === "bengaluru")));

const roundUp = (n) => Math.ceil(n / 5000) * 5000;

export const withRealCounts = (tours, facets) => ({
  ...facets,
  priceMax: roundUp(Math.max(...tours.map((t) => t.startingPrice || 0), 5000)),
  pricePresets: facets.pricePresets.filter((p) => tours.some((t) => t.startingPrice != null && t.startingPrice >= p.min && t.startingPrice <= p.max)),
  durationPresets: facets.durationPresets.filter((d) => {
    const [min, max] = parseDayRange(d.label);
    return tours.some((t) => t.days >= min && t.days <= max);
  }),
  departureCities: dedupeAliases(recount(facets.departureCities, tours, matchDeparture)),
  joiningLeaving: facets.joiningLeaving ? recount(facets.joiningLeaving, tours, matchJoining) : null,
  packageTypes: [{ label: "All", count: tours.length }, ...recount(facets.packageTypes.filter((p) => p.label !== "All"), tours, matchPackageType)],
  speciality: recount(facets.speciality, tours, matchSpeciality),
  countries: recount(facets.countries, tours, matchPlace),
  cities: recount(facets.cities, tours, matchCity),
});

const anyOf = (set, t, fn) => set.size === 0 || [...set].some((v) => fn(t, v));

export const applyFilters = (tours, f, priceMax) =>
  tours.filter((t) => {
    const priceActive = f.price[0] !== 0 || f.price[1] !== priceMax;
    if (priceActive && (t.startingPrice == null || t.startingPrice < f.price[0] || t.startingPrice > f.price[1])) return false;
    if (f.duration) {
      const [min, max] = parseDayRange(f.duration);
      if (t.days < min || t.days > max) return false;
    }
    if (!matchPackageType(t, f.packageType)) return false;
    if ((f.dateFrom || f.dateTo) && !departsBetween(t, f.dateFrom, f.dateTo)) return false;
    return (
      anyOf(f.speciality, t, matchSpeciality) &&
      anyOf(f.departure, t, matchDeparture) &&
      anyOf(f.joining, t, matchJoining) &&
      anyOf(f.countries, t, matchPlace) &&
      anyOf(f.cities, t, matchCity)
    );
  });

// A tour is a deal when its starting price is below the regular price of its other departures.
export const getDeal = (t) => {
  const prices = (t.dates || []).map((d) => d.price).filter((p) => p != null);
  const regular = prices.length ? Math.max(...prices) : null;
  if (t.startingPrice == null || regular == null || regular <= t.startingPrice) return null;
  return { oldPrice: regular, save: regular - t.startingPrice, pct: Math.round(((regular - t.startingPrice) / regular) * 100) };
};
